import { observer } from 'mobx-react-lite';
import type { JSX, ReactNode } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type {
  EmailBaseCampaign,
  GoogleAdsSmartCampaign,
  IConfig as IBaseConfig,
  IReportAttributes,
  ReportModel,
} from '@feathr/blackbox';
import { CampaignClass } from '@feathr/blackbox';
import { ContextMenu, CopyToClipboardButton } from '@feathr/components';
import type { IUseReportExportReturn } from '@feathr/extender/hooks';
import { useReportActions } from '@feathr/extender/hooks';
import { getIconForAction } from '@feathr/hooks';
import { ConfigModal, ExportModal, ShareableLinksModal } from '@feathr/report_components';

export interface IReportActionsContextMenuProps<
  IAttributes extends IReportAttributes,
  IConfig extends IBaseConfig,
> extends IUseReportExportReturn<IConfig> {
  model: ReportModel<IAttributes>;
  /** Additional menu items to insert at the end of the context menu */
  menuItems?: ReactNode;
  /** Content to trigger with the additional menu items */
  children?: ReactNode;
}

function ReportActionsContextMenu<
  IAttributes extends IReportAttributes,
  IConfig extends IBaseConfig,
>({
  model,
  menuItems,
  children,
  startFormatted,
  endFormatted,
  reportLink,
  hasGoals,
  mode,
  config,
  handlers: exportHandlers,
}: Readonly<IReportActionsContextMenuProps<IAttributes, IConfig>>): JSX.Element {
  const { t } = useTranslation();
  const { isCustomizeModalOpen, isLinksModalOpen, isExportModalOpen, handlers } =
    useReportActions();

  function isEmailCampaign(
    model: ReportModel<IAttributes> | EmailBaseCampaign,
  ): model is EmailBaseCampaign {
    return [
      CampaignClass.PinpointEmail,
      CampaignClass.SmartPinpointEmail,
      CampaignClass.AutoPinpointEmail,
      CampaignClass.Drip,
      CampaignClass.MailrailSingleSend,
      CampaignClass.MailrailAutoSend,
      CampaignClass.MailrailDrip,
    ].includes((model as EmailBaseCampaign).get('_cls'));
  }

  function isPinpointDripCampaign(model: ReportModel<IAttributes>): boolean {
    return model.get('_cls') === CampaignClass.Drip;
  }

  function isGoogleCampaign(
    model: ReportModel<IAttributes> | GoogleAdsSmartCampaign,
  ): model is GoogleAdsSmartCampaign {
    return (model as GoogleAdsSmartCampaign).get('_cls') === CampaignClass.GoogleAdsSmart;
  }

  function isInvitesCampaign(model: ReportModel<IAttributes>): boolean {
    return model.get('_cls') === CampaignClass.Referral;
  }

  return (
    <>
      <ContextMenu buttonType={'icon-outlined'} iconName={'ellipsis'}>
        <ContextMenu.Label>{t('Report')}</ContextMenu.Label>
        <ContextMenu.Item
          onClick={handlers.toggleCustomizeModalOpen}
          prefix={getIconForAction('customize')}
        >
          {t('Customize')}
        </ContextMenu.Item>
        <ContextMenu.Item
          onClick={handlers.toggleLinksModalOpen}
          prefix={getIconForAction('shortLinks')}
        >
          {t('Manage short links')}
        </ContextMenu.Item>
        <CopyToClipboardButton
          component={'ContextMenuItem'}
          successMessage={t('Copied report link to clipboard')}
          t={t}
          text={reportLink}
        >
          {t('Copy link')}
        </CopyToClipboardButton>
        <ContextMenu.Item
          onClick={handlers.toggleExportModalOpen}
          prefix={getIconForAction('export')}
        >
          {t('Export to CSV')}
        </ContextMenu.Item>
        {menuItems}
      </ContextMenu>
      {children}
      {isCustomizeModalOpen && (
        <ConfigModal<IConfig>
          config={config}
          hasGoals={hasGoals}
          isEmailCampaign={isEmailCampaign(model)}
          isGoogleCampaign={isGoogleCampaign(model)}
          isInvitesCampaign={isInvitesCampaign(model)}
          isPinpointDripCampaign={isPinpointDripCampaign(model)}
          onChange={exportHandlers.onConfigChange}
          onClose={handlers.closeAndSaveReportConfig}
        />
      )}
      {isLinksModalOpen && (
        <ShareableLinksModal
          config={config}
          endDate={endFormatted}
          mode={mode}
          model={model}
          onClose={handlers.toggleLinksModalOpen}
          startDate={startFormatted}
        />
      )}
      {isExportModalOpen && (
        <ExportModal
          onClose={handlers.toggleExportModalOpen}
          onConfirm={exportHandlers.export}
          title={t('Export Report Data')}
        />
      )}
    </>
  );
}

export default observer(ReportActionsContextMenu);
